import { ApolloQueryResult } from '@apollo/client';

import { InternetProduct, MvEnv, OAuthClient, ProductInstanceStatus, Query } from '@mfe/shared/schema-types';

import { TokenInfo } from './authUserSlice';
import { queryString } from '@viasat/res-apps-lib/build/utils';

export const TOKEN_INFO_COOKIE_NAME = 'tokenInfo';
export const USER_ID_COOKIE_NAME = 'user_id';
export const DISMISS_COOKIE_NAME = 'dismiss';
export const REDIRECT_PATH_COOKIE_NAME = 'redirectPath';
export const MS_IN_A_MINUTE = 60000;
export type LoginType = 'password' | 'cookie';

const OLD_LOGOUT_PATH = '/federation/logout';
// MySSO OAuth Authorize URL: https://wiki.viasat.com/display/IAM/SSO+-+OAuth+Integration#SSOOAuthIntegration-3.2Authorize
const OLD_AUTHORIZE_PATH = '/federation/oauth2/authorize';
const OLD_RESPONSE_TYPE = 'code';
const OLD_SCOPE = ['uid'];

const LOGOUT_PATH = '/v1/logout';
const AUTHORIZE_PATH = '/v1/authorize';
const RESPONSE_TYPE = 'code';
const SCOPE = ['openid', 'profile', 'email', 'offline_access', 'sbn'];

export const getAuthUrls = (
  client: OAuthClient,
  baseUrl: string,
  idToken?: string
): { loginUrl: string; logoutUrl: string } => {
  if (baseUrl.includes('mysso')) {
    const loginUrl = `${baseUrl}${OLD_AUTHORIZE_PATH}?${queryString({
      client_id: client.clientId,
      response_type: OLD_RESPONSE_TYPE,
      redirect_uri: client.redirectUrl ?? '',
      scope: OLD_SCOPE.join(' '),
    })}`;
    const logoutUrl = `${baseUrl}${OLD_LOGOUT_PATH}?${queryString({
      goto: loginUrl,
    })}`;
    return { loginUrl, logoutUrl };
  }

  const loginUrl = `${baseUrl}${AUTHORIZE_PATH}?${queryString({
    client_id: client.clientId,
    response_type: RESPONSE_TYPE,
    redirect_uri: client.redirectUrl ?? '',
    scope: SCOPE.join(' '),
    state: 'asdfasdf',
  })}`;
  const logoutUrl = `${baseUrl}${LOGOUT_PATH}?${queryString({
    id_token_hint: idToken ?? '',
    post_logout_redirect_uri: loginUrl,
  })}`;
  return { loginUrl, logoutUrl: idToken ? logoutUrl : loginUrl };
};

const getURLParams = (url: string): Record<string, string> => {
  const re = /[?&]?([^=]+)=([^&]*)/g;
  const qs = url.replace(/.*\?/, '').replace(/\+/g, ' ');
  const params: Record<string, string> = {};
  let tokens: RegExpExecArray | null;

  // Loop over entire url
  while ((tokens = re.exec(qs))) {
    params[decodeURIComponent(tokens[1])] = decodeURIComponent(tokens[2]);
  }

  return params;
};

export const retrieveAuthCode = (url: string | undefined, client: OAuthClient): string | null => {
  if (!url) return null;
  const params = getURLParams(url);
  if (url.startsWith(client.redirectUrl ?? '') && params['code']) {
    return params['code'];
  }

  return null;
};

export const parseTokenInfoCookie = (token?: string): TokenInfo | null => {
  if (!token) return null;

  try {
    const parsedToken: TokenInfo = JSON.parse(token);
    const expirationTime = new Date(parsedToken.accessTokenExpirationTime);
    if (!parsedToken.accessToken || isNaN(expirationTime.getTime())) {
      return null;
    }

    return parsedToken;
  } catch {
    return null;
  }
};

const getMFEEnvFromUrl = (url: string): MvEnv => {
  if (url.includes(MvEnv.Dev)) return MvEnv.Dev;
  if (url.includes(MvEnv.Test)) return MvEnv.Test;
  if (url.includes(MvEnv.Preprod)) return MvEnv.Preprod;
  if (url.includes(MvEnv.Prod)) return MvEnv.Prod;
  return MvEnv.DevLocal;
};

export const getTokenFromUrl = () => {
  const url = new URL(window.location.href);
  const env = getMFEEnvFromUrl(window.location.href);
  const urlSearchParams: URLSearchParams = new URLSearchParams(url.search);
  const token = urlSearchParams.get('token') as string;
  const productInstanceId = urlSearchParams.get('productInstanceId');
  const partyId = urlSearchParams.get('partyId');

  return { token, productInstanceId, partyId, env };
};

const productStatusSortOrder = (status: string): number => {
  switch (status) {
    case ProductInstanceStatus.Active:
      return 0;
    case ProductInstanceStatus.Preinstall:
      return 1;
    case ProductInstanceStatus.Other:
    case ProductInstanceStatus.Error:
      return 2;
    case ProductInstanceStatus.Suspended:
      return 3;
    default:
      return 4;
  }
};

const productStatusSortOrderPlanChanged = (status: string): number => {
  // Immediately after a change plan, the new plan will have status "OTHER"
  switch (status) {
    case ProductInstanceStatus.Other:
    case ProductInstanceStatus.Error:
      return 0;
    case ProductInstanceStatus.Active:
      return 1;
    case ProductInstanceStatus.Preinstall:
      return 2;
    case ProductInstanceStatus.Suspended:
      return 3;
    default:
      return 4;
  }
};

const sortProductInstancesByStatus = (products: InternetProduct[], planChanged?: boolean) => {
  return [...products].sort((a, b) => {
    const locationOrderA = planChanged
      ? productStatusSortOrderPlanChanged(a?.productInstanceStatus ?? '')
      : productStatusSortOrder(a?.productInstanceStatus ?? '');
    const locationOrderB = planChanged
      ? productStatusSortOrderPlanChanged(b?.productInstanceStatus ?? '')
      : productStatusSortOrder(b?.productInstanceStatus ?? '');

    return locationOrderA - locationOrderB;
  });
};

export const parseUser = (data: Partial<ApolloQueryResult<Query>['data']>, planChanged?: boolean) => {
  const bepInputs = data.getBEPInputs;

  const productSortedByStatus = sortProductInstancesByStatus(
    (bepInputs?.internetProducts as InternetProduct[]) ?? [],
    planChanged
  );

  return {
    accountNumber: bepInputs?.accountNumber ?? '',
    productInstanceId: productSortedByStatus?.[0]?.productInstanceId ?? null,
    productTypeId: productSortedByStatus?.[0]?.productTypeId ?? null,
    productKind: productSortedByStatus?.[0]?.kind ?? null,
    productInstanceStatus: productSortedByStatus?.[0]?.productInstanceStatus ?? ProductInstanceStatus.Other,
    partyId: bepInputs?.partyId ?? '',
    relnId: bepInputs?.relnId ?? '',
    serviceAddress: bepInputs?.address,
    coordinates: bepInputs?.coordinates,
    isSoftSuspended: !!bepInputs?.internetProducts?.find((productInstance) => productInstance?.isSoftSuspended),
    hasCaf: !!bepInputs?.internetProducts?.find((productInstance) => productInstance?.hasCaf),
    hasErrorProductInstance: bepInputs?.internetProducts?.[0]?.productInstanceStatus === ProductInstanceStatus.Error,
    indoorUnit: bepInputs?.indoorUnit,
    isFiberPreconnected: bepInputs?.isFiberPreconnected,
    fiberCityId: bepInputs?.fiberCityId,
    fiberAddressId: bepInputs?.fiberAddressId,
  };
};
